/**
 * Homepage-only animation entry (Phase 4 signature layer).
 *
 * Stands up the Phase 4 JS surface: the Lenis + ScrollTrigger wiring (RESEARCH
 * Pattern 2) and a gsap.matchMedia three-branch structure (RESEARCH Pattern 5)
 * whose reduced-motion branch is a no-op FROM THE FIRST COMMIT (HOME-05). The
 * desktop branch runs the pinned scrub bloom (warm hero-field behind it) that
 * builds, grows and hands off to Our Story via the shared-scroll morph; the mobile
 * branch runs the in-flow, no-pin bloom. The supporting layer (section reveals,
 * marquee drift, parallax, timeline line-draw, CTA reprise) is implemented below in
 * buildReveals / buildMarquee / buildTimelineDraw / buildReprise.
 *
 * Load-bearing rules honoured here:
 * - Enqueued on the front page only (inc/assets.php is_front_page gate), so
 *   subpages carry zero animation cost.
 * - src/main.js stays GSAP-free; all motion lives in this separate entry.
 * - No hidden from-states in CSS. Every from-state belongs in gsap.set/gsap.from
 *   inside a matchMedia branch (RESEARCH Pitfall 2).
 * - The bloom is inlined via a Vite ?raw import so per-petal/thread staggering is
 *   possible; the hero <img> stays the untouched static/no-JS fallback
 *   (RESEARCH Pattern 4).
 *
 * @package foodcreations
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import './css/home-motion.css';
import bloomMarkup from './assets/bloom/bloom.svg?raw';

// Photographic spice textures for the clip-masked petal skin (Plan 04-04). Vite
// fingerprints each on import; the URLs are only injected into the bloom markup
// mounted INSIDE a motion branch, and the .fc-petal-photo layer rests at opacity
// 0, so no-JS and reduced-motion visitors never see it. The desktop timeline
// crossfades it to 0 on resolve so the settled frame is the flat logo mark.
import chilliUrl from './assets/bloom-petals/spice-chilli.webp';
import saffronUrl from './assets/bloom-petals/spice-saffron.webp';
import herbUrl from './assets/bloom-petals/spice-herb.webp';

// Background-removed ingredient accents that drift in parallax around the bloom
// (desktop branch only). Vite fingerprints each on import; they are only appended
// to the DOM inside the desktop branch, so mobile and reduced-motion visitors
// never fetch them.
import accentSaffron from '../assets/hero-accents/saffron-threads.webp';
import accentStarAnise from '../assets/hero-accents/star-anise.webp';
import accentRedChilli from '../assets/hero-accents/red-chilli.webp';
import accentCardamom from '../assets/hero-accents/cardamom.webp';

// The warm ingredient-macro field behind the bloom (desktop/tablet only). Imported
// through import.meta.url so Vite fingerprints it; the URL is only ever assigned to
// an element created INSIDE the desktop branch, so reduced-motion and mobile
// visitors never fetch it.
const heroFieldUrl = new URL( '../assets/hero-field/ingredient-macro.webp', import.meta.url ).href;

// DrawSVGPlugin drives the petal/thread stroke-draw unfurl (Wave 2). It is
// registered alongside ScrollTrigger now that the bloom morph ships.
gsap.registerPlugin( ScrollTrigger, DrawSVGPlugin );

/*
 * Lenis smooth scroll is itself motion, so it is booted ONLY when the visitor
 * has not asked for reduced motion. Under prefers-reduced-motion the page keeps
 * native scroll and stays the exact static Phase 3 experience (HOME-05). The
 * wiring is the canonical Lenis + ScrollTrigger integration (RESEARCH Pattern 2):
 * a single RAF loop driven by the gsap ticker, with lag smoothing off so a
 * scrubbed morph never catches up in a jump.
 */
const prefersReduced = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

if ( ! prefersReduced ) {
	const lenis = new Lenis( { autoRaf: false } );
	lenis.on( 'scroll', ScrollTrigger.update );
	gsap.ticker.add( ( time ) => lenis.raf( time * 1000 ) ); // gsap ticker is seconds; Lenis wants ms.
	gsap.ticker.lagSmoothing( 0 );
}

/**
 * Inject the animatable inline bloom into the hero morph anchor and hide the
 * static <img> fallback once it is in. References bloomMarkup so the ?raw import
 * stays live and is inlined into the built chunk (must not be tree-shaken). The
 * <img> is only hidden after the inline SVG mounts, so no-JS visitors keep the
 * resolved mark. Returns the injected <svg> (or null if the anchor is absent).
 *
 * @return {SVGElement|null} The injected inline bloom, or null.
 */
function mountBloom() {
	const slot = document.querySelector( '[data-fc-morph="bloom"]' );
	if ( ! slot ) {
		return null;
	}
	// Swap the petal-photo href tokens for the fingerprinted built texture URLs
	// before the markup is parsed, so each clip-masked petal fills with its spice
	// texture. The tokens live in bloom.svg; the URLs come from the Vite imports.
	const markup = bloomMarkup
		.replaceAll( '__CHILLI_URL__', chilliUrl )
		.replaceAll( '__SAFFRON_URL__', saffronUrl )
		.replaceAll( '__HERB_URL__', herbUrl );
	slot.insertAdjacentHTML( 'beforeend', markup );
	const svg = slot.querySelector( 'svg' );
	const img = slot.querySelector( 'img' );
	if ( svg && img ) {
		img.style.display = 'none';
	}
	return svg;
}

/**
 * Restrained scroll-driven storytelling (RESEARCH Pattern 6): the section
 * rise-and-fade reveals plus the staggered service-card and timeline-step
 * cascades. Called from BOTH motion branches. Every from-state lives here in
 * gsap.from, never in CSS (RESEARCH Pitfall 2), so no-JS and reduced-motion
 * visitors never have content hidden. toggleActions 'play none none reverse'
 * plays the reveal on entry and reverses it on scroll-up.
 *
 * @param {boolean} skipStory When true (desktop morph), the Our Story reveal is
 *   NOT built here: the desktop hero handoff owns Story's entrance, and, crucially,
 *   this reveal would give Story's inner a transform (its own stacking context) that
 *   paints OVER the fixed pinned hero and leaks Story through during the pin. Leaving
 *   Story flat keeps it reliably BEHIND the hero until the hero fades to reveal it.
 */
function buildReveals( skipStory ) {
	// Calm content sections rise + fade in as a block on entry: the trigger and
	// the moved element are the section INNER, so the full-bleed background band
	// stays put while its content lifts. These sections carry no staggered children.
	// On desktop Story is omitted (the morph reveals it, and a transform here would
	// leak it over the pinned hero); mobile / reduced-motion still reveal it normally.
	const calmScenes = skipStory
		? '[data-fc-scene="products"], [data-fc-scene="trust"]'
		: '[data-fc-scene="story"], [data-fc-scene="products"], [data-fc-scene="trust"]';
	gsap.utils.toArray( calmScenes ).forEach( ( section ) => {
		const inner = section.firstElementChild;
		if ( ! inner ) {
			return;
		}
		gsap.from( inner, {
			scrollTrigger: { trigger: section, start: 'top 85%', toggleActions: 'play none none reverse' },
			y: 24,
			opacity: 0,
			duration: 0.6,
			ease: 'power2.out',
		} );
	} );

	// The four service cards and five timeline steps cascade as ONE gesture: the
	// trigger is the SECTION container and the [data-fc-reveal] children stagger,
	// so each section reads as a single choreographed reveal rather than four or
	// five independent ones.
	gsap.utils.toArray( '[data-fc-scene="services"], [data-fc-scene="timeline"]' ).forEach( ( section ) => {
		const items = gsap.utils.toArray( section.querySelectorAll( '[data-fc-reveal]' ) );
		if ( ! items.length ) {
			return;
		}
		// once + clearProps: play the cascade a SINGLE time and strip the inline
		// transform when it lands. The previous 'play none none reverse' could toggle
		// mid-stagger and leave the last card (menu-development) parked a few px low at
		// rest; playing once to completion and clearing the transform guarantees every
		// card settles at its natural, aligned position.
		gsap.from( items, {
			scrollTrigger: { trigger: section, start: 'top 85%', once: true },
			y: 24,
			opacity: 0,
			duration: 0.6,
			ease: 'power2.out',
			stagger: 0.1,
			clearProps: 'transform',
		} );
	} );
}

/**
 * The slow, seamless site-wide ticker drift (RESEARCH Pattern 6). The ticker
 * markup already duplicates the statement into two .fc-ticker__group elements
 * (the second aria-hidden), so translating by exactly one group width and
 * repeating is seamless with no gap. This is a continuous repeat: -1 tween, NOT a
 * ScrollTrigger. justify-content flex-start is applied by JS ONLY, so no-JS and
 * reduced-motion visitors keep the centred, frozen statement from main.css. The
 * width is measured after fonts settle so the loop distance is correct. Registers
 * its teardown (kill the tween, restore the centred rest state) on the cleanups
 * array, because the tween is created asynchronously and so is not tracked by the
 * matchMedia context.
 *
 * @param {Array<Function>} cleanups Teardown callbacks run when the branch stops matching.
 */
function buildMarquee( cleanups ) {
	const track = document.querySelector( '[data-fc-marquee]' );
	if ( ! track ) {
		return;
	}
	const group = track.querySelector( '.fc-ticker__group' );
	if ( ! group ) {
		return;
	}

	let tween = null;
	let torn = false;

	const band = track.closest( '.fc-ticker' ) || track;
	const pause = () => tween?.pause();
	const resume = () => tween?.resume();

	const start = () => {
		if ( torn ) {
			return; // The branch was torn down (e.g. reduced motion toggled on) before fonts settled.
		}
		track.style.justifyContent = 'flex-start';
		const styles = getComputedStyle( track );
		const gap = parseFloat( styles.columnGap || styles.gap ) || 0;
		// The track gap is now 0 and the period spacing lives in the group's trailing
		// padding, which getBoundingClientRect already includes. The gap term is kept
		// so this stays correct if the spacing ever moves back onto the track.
		const distance = group.getBoundingClientRect().width + gap;
		// 15s matches the CSS drift on every other page, so the band reads at one
		// speed site-wide rather than faster on the homepage.
		tween = gsap.to( track, { x: -distance, duration: 15, ease: 'none', repeat: -1 } );

		// Pause while pointed at or focused, so a linked message is actually clickable
		// (mirrors the :hover / :focus-within rule the CSS path uses).
		band.addEventListener( 'pointerenter', pause );
		band.addEventListener( 'pointerleave', resume );
		band.addEventListener( 'focusin', pause );
		band.addEventListener( 'focusout', resume );
	};

	if ( document.fonts && document.fonts.ready ) {
		document.fonts.ready.then( start );
	} else {
		start();
	}

	cleanups.push( () => {
		torn = true;
		tween?.kill();
		band.removeEventListener( 'pointerenter', pause );
		band.removeEventListener( 'pointerleave', resume );
		band.removeEventListener( 'focusin', pause );
		band.removeEventListener( 'focusout', resume );
		track.style.justifyContent = ''; // Back to the main.css default.
		gsap.set( track, { clearProps: 'transform' } );
	} );
}

/**
 * Timeline connector line-draw (RESEARCH Open Question 2). The existing static
 * CSS connector (.fc-home-timeline__list::before, a faint saffron guide) and the
 * numbered counter nodes are left UNTOUCHED (the decoupling rule). JS overlays a
 * dedicated motion-only connector element boxed exactly over that guide and draws
 * it in with a scaleY from 0 (transform-origin top) synced to the step cascade,
 * so the crisp line draws down as the numbered nodes appear along it. The
 * overlay's box lives in home-motion.css; the from-state (scaleY 0) lives here.
 * scaleY of a dedicated element was chosen over an inline-SVG DrawSVG overlay so
 * the existing counter/node CSS is not disturbed (Open Question 2 fallback). The
 * injected element is aria-hidden and removed on branch change (via cleanups).
 *
 * @param {Array<Function>} cleanups Teardown callbacks run when the branch stops matching.
 */
function buildTimelineDraw( cleanups ) {
	const timeline = document.querySelector( '[data-fc-scene="timeline"]' );
	if ( ! timeline ) {
		return;
	}
	const list = timeline.querySelector( '.fc-home-timeline__list' );
	if ( ! list ) {
		return;
	}
	const draw = document.createElement( 'span' );
	draw.className = 'fc-timeline-draw'; // Motion-only namespace (parallels the JS-injected hero field layer), styled in home-motion.css.
	draw.setAttribute( 'aria-hidden', 'true' );
	list.appendChild( draw ); // The list is position: relative (home.css), so the overlay boxes over the ::before guide.
	cleanups.push( () => draw.remove() );

	// The connector runs vertically on the mobile rail and horizontally on the
	// desktop process strip (matching the home.css 64rem breakpoint), so draw the
	// overlay along its axis: scaleX from the left on desktop, scaleY from the top
	// on mobile. The from-state lives here (never in CSS) so nothing is hidden without JS.
	const horizontal = window.matchMedia( '(min-width: 64rem)' ).matches;
	gsap.from( draw, {
		scrollTrigger: { trigger: timeline, start: 'top 85%', toggleActions: 'play none none reverse' },
		...( horizontal
			? { scaleX: 0, transformOrigin: 'left center' }
			: { scaleY: 0, transformOrigin: 'center top' } ),
		duration: 0.9,
		ease: 'power1.inOut',
	} );
}

/**
 * Closing CTA reprise (RESEARCH Open Question 3): a gentle, quicker scale +
 * opacity breath on the reprise mark container (.fc-home-cta__mark,
 * [data-fc-morph="bloom-reprise"]) as the CTA section enters. This understated
 * echo bookends the page WITHOUT repeating the full hero spectacle: no inline SVG
 * is injected here, so the hero stays the singular signature moment. The
 * from-state lives in JS; toggleActions reverses the breath on scroll-up.
 */
function buildReprise() {
	const mark = document.querySelector( '[data-fc-morph="bloom-reprise"]' );
	if ( ! mark ) {
		return;
	}
	gsap.from( mark, {
		scrollTrigger: { trigger: '[data-fc-scene="cta"]', start: 'top 80%', toggleActions: 'play none none reverse' },
		scale: 0.85,
		opacity: 0,
		transformOrigin: 'center center',
		duration: 0.5,
		ease: 'power2.out',
	} );
}

/*
 * The reduced-motion branch exists from the first tween, so no motion ever ships
 * without its opt-out (HOME-05). matchMedia creates an internal gsap.context, so
 * every tween/ScrollTrigger made inside a branch auto-reverts when the branch
 * stops matching; the returned cleanup also removes the injected SVG so a
 * re-run never stacks duplicates. The no-preference condition is composed INTO
 * the desktop/mobile queries so toggling reduced motion mid-session tears motion
 * down cleanly and re-renders static.
 */
const mm = gsap.matchMedia();

mm.add(
	{
		isDesktop: '(min-width: 64rem) and (prefers-reduced-motion: no-preference)',
		isMobile: '(max-width: 63.999rem) and (prefers-reduced-motion: no-preference)',
		reduce: '(prefers-reduced-motion: reduce)',
	},
	( ctx ) => {
		const { isDesktop, isMobile, reduce } = ctx.conditions;

		if ( reduce ) {
			return; // No tweens, no pin, no parallax: the page stays the static Phase 3 end-state.
		}

		const svg = mountBloom();
		let heroField = null;
		const cleanups = []; // Teardown for async tweens and JS-injected DOM (marquee, timeline connector).

		/*
		 * Both motion branches unfurl the SAME resolved inline bloom, so every
		 * timeline END equals the settled mark (which equals logo-mark.svg): there
		 * is no jump between motion-on, motion-off, mobile and desktop. Refresh
		 * ScrollTrigger once layout is final (fonts, the injected SVG, the field
		 * image) so the pin distance is measured against the real settled hero
		 * (RESEARCH Pitfall 4).
		 */
		const refresh = () => ScrollTrigger.refresh();
		window.addEventListener( 'load', refresh, { once: true } );
		if ( document.fonts && document.fonts.ready ) {
			document.fonts.ready.then( refresh );
		}

		if ( isDesktop ) {
			/*
			 * Motion INTENSITY constants, grouped at the top of the desktop branch so
			 * the feel can be dialed after a live look without hunting through the
			 * timeline. Kept deliberate and unbusy per the Phase 3 sign-off: a soft
			 * settle, a gentle field breathe, and a slow accent drift, never a
			 * carnival. Every value resolves to the settled logo-mark end-state, so
			 * none of this changes the no-jump resolve.
			 */
			const PETAL_PHOTO_SETTLE = 1.06; // the photo skin eases from a hair oversized down to its resolved size.
			const FIELD_BREATHE_TO = 1.2; // the warm field breathes from scale 1.15 up to this across the pinned hold.
			const ACCENTS = [
				{ url: accentSaffron, size: '7.5rem', pos: { top: '13%', left: '7%' }, drift: { yPercent: 46, xPercent: -6 } },
				{ url: accentStarAnise, size: '6rem', pos: { top: '18%', right: '8%' }, drift: { yPercent: 68, xPercent: 5 } },
				{ url: accentRedChilli, size: '5.5rem', pos: { bottom: '16%', left: '11%' }, drift: { yPercent: -40, xPercent: 4 } },
				{ url: accentCardamom, size: '6.5rem', pos: { bottom: '21%', right: '12%' }, drift: { yPercent: -60, xPercent: -5 } },
			];

			/*
			 * Pinned-sequence tuning, grouped so the feel is dialable at the gate without
			 * hunting through the timeline. Per the morph rework the ONE pin runs a FIVE
			 * beat sequence, all scroll-driven (scrubbed): (1) the BUILD, where the flat
			 * logo mark unfurls into the full photographic spice bloom (threads draw, photo
			 * petals bloom in, the skin settling to size); (2) a BRIEF hold on the
			 * formed bloom; (3) the copy lifts, then a snappy modest GROW (the mark grows
			 * to ~GROW_SCALE x its resting size, a prominent flourish, NOT a screen-fill)
			 * DURING WHICH the photo skin resolves off to the flat vector mark, so the big
			 * mark is scalable SVG in brand colours rather than an upscaled texture;
			 * (4) the TRAVEL, the signature moment: the grown vector mark shrinks and
			 * glides into the Story emblem
			 * slot, and the hero's opaque layers (cream background, warm field, legibility
			 * veil) fade out AROUND the moving mark so Story appears around the flight;
			 * (5) the LANDING: a matched-geometry crossfade seats the travelled bloom onto
			 * Story's static emblem, and the mark then RIDES Story's remaining rise so the
			 * pair stay seated together through the unpin. The logo now has a DESTINATION
			 * (it lands in Our Story) instead of dissolving into nothing, and the pin only
			 * releases once Story is fully framed, so Story can not be scrolled past.
			 * These are timeline time units: scrub maps the whole timeline linearly across
			 * HERO_PIN, so they are proportions, not seconds. TL_END is the single source
			 * for the total, so the ride distance always closes exactly at the pin end.
			 */
			const HERO_PIN = '+=150%'; // longer runway: the build is quicker and the grow + travel own most of the scroll (the morph is the star, not the build).
			const GROW_SCALE = 3.6; // FIXED grow target: the mark grows to ~3.6x its resting size (a prominent flourish, NOT a screen-fill).
			const D_BUILD = 0.7; // BUILD: quicker than before, so fast scrollers reach the morph itself sooner (the old 43% share buried the payoff).
			const HOLD = 0.2; // brief hold on the formed spice bloom before anything moves.
			const D_COPY = 0.25; // the copy / accents / field lift is quick, finishing just as the grow starts.
			const COPY_AT = D_BUILD + HOLD; // 0.9: the copy/accents/field lift begins after the build and the brief hold.
			const GROW_AT = COPY_AT + D_COPY; // 1.15: the grow starts right as the copy finishes lifting.
			const D_GROW = 0.45; // snappy modest grow: short, so it reads as a quick flourish rather than a slow cover.
			const TRAVEL_AT = GROW_AT + D_GROW; // 1.6: the grown bloom starts its flight into Story.
			const D_TRAVEL = 0.6; // the flight owns the longest single beat: this is the morph the visitor came for.
			const TL_END = 2.4; // total timeline length; the crossfade closes exactly here (the pin end).
			const REVEAL_AT = TRAVEL_AT + 0.1; // 1.7: the hero's opaque layers fade DURING the flight so Story appears around the moving mark.
			const D_REVEAL = 0.35; // unhurried layer fade: Story eases in around the flight rather than popping.
			// The crossfade sits at the VERY END of the timeline, deliberately. The scrubbed
			// timeline lags real scroll (scrub: 1), so in normal scrolling the visitor's
			// scroll is already resting at or past the pin end while these last beats render:
			// Story is parked at its final frame and the swap plays out against STATIC
			// geometry, dead-on. Any earlier (the first cut seated the mark at LAND time and
			// scroll-coupled a follow ride) and the lag makes the mark land visibly low, then
			// slide up to correct, which read as a miss.
			const LAND_AT = TL_END - 0.08; // 2.32: the static emblem starts fading in under the hovering mark.
			const D_LAND = 0.06; // matched-geometry crossfade: inline bloom out, static emblem in.

			/*
			 * Warm ingredient-macro field behind the bloom (desktop/tablet only).
			 * Created here so the asset is fetched ONLY on desktop; the <img> is
			 * decoded async as decorative chrome, so the hero headline stays the
			 * natural LCP (RESEARCH Pitfall 7). Its decode() also settles layout, so
			 * we refresh once it resolves.
			 */
			const hero = document.querySelector( '[data-fc-scene="hero"]' );
			if ( hero ) {
				heroField = document.createElement( 'div' );
				heroField.className = 'fc-hero-field';
				heroField.setAttribute( 'aria-hidden', 'true' );
				const fieldImg = document.createElement( 'img' );
				fieldImg.className = 'fc-hero-field__img';
				fieldImg.decoding = 'async';
				fieldImg.alt = '';
				fieldImg.src = heroFieldUrl;
				heroField.appendChild( fieldImg );
				hero.insertAdjacentElement( 'afterbegin', heroField );
				fieldImg.decode().then( refresh ).catch( () => {} );
			}

			/*
			 * THE SHARED-SCROLL HANDOFF ("Story rises in under the logo", zero gap). A pinned
			 * full-viewport hero reserves a pin-spacer of pinDistance + heroHeight; without
			 * help, the hero's own height (empty cream) scrolls past after the morph before
			 * Story, which read as a dead gap. The fix is to pull the following Story section
			 * UP by exactly the hero's rendered height so it sits at the PIN-RELEASE point:
			 * then, during the pin, Story rises up behind the fixed hero and is already at the
			 * top the instant the pin releases (no gap). Everything after Story shifts up with
			 * it (a margin moves the whole downstream flow), so there is no internal gap either.
			 *
			 * The two things that broke this before are both fixed here:
			 *   1. The pull is a STABLE CSS-computed value (home-motion.css: -100svh minus the
			 *      hero's exact padding), enabled by the fc-hero-morph class added below. No
			 *      runtime getBoundingClientRect (the old mid-scroll measurement returned
			 *      phantom heights and mis-pulled Story).
			 *   2. The hero carries an explicit z-index (home-motion.css, below the sticky
			 *      header's 100) so the rising Story stays HIDDEN behind the fixed hero until
			 *      the reveal, instead of leaking over it.
			 * The class (not an inline margin) means zero measurement here, and it is
			 * DESKTOP-MOTION-ONLY: reduced-motion / mobile / no-JS never add it, so they keep
			 * plain document flow. Removed on branch teardown so a reduced-motion toggle
			 * restores flow.
			 */
			document.documentElement.classList.add( 'fc-hero-morph' );
			cleanups.push( () => document.documentElement.classList.remove( 'fc-hero-morph' ) );

			/*
			 * Floating background-removed ingredient accents (DESKTOP ONLY). Created
			 * here, inside isDesktop, so mobile and reduced-motion visitors never fetch
			 * them. Each is decorative (aria-hidden, pointer-events none via the
			 * class), decoded async so it never competes with the hero LCP, absolutely
			 * positioned around the bloom, and drifts on its own slow scrub parallax
			 * (varied depth/speed) for a sense of depth. The parallax from-state lives
			 * here in gsap, never in CSS. The elements are removed on branch teardown.
			 */
			const accentEls = [];
			if ( hero ) {
				ACCENTS.forEach( ( accent ) => {
					const img = document.createElement( 'img' );
					img.className = 'fc-hero-accent';
					img.decoding = 'async';
					img.alt = '';
					img.setAttribute( 'aria-hidden', 'true' );
					img.src = accent.url;
					img.style.inlineSize = accent.size;
					Object.entries( accent.pos ).forEach( ( [ edge, value ] ) => {
						img.style.setProperty( edge, value );
					} );
					hero.appendChild( img );
					accentEls.push( img );

					gsap.to( img, {
						yPercent: accent.drift.yPercent,
						xPercent: accent.drift.xPercent,
						ease: 'none',
						scrollTrigger: {
							trigger: '[data-fc-scene="hero"]',
							start: 'top top',
							end: 'bottom top',
							scrub: true,
						},
					} );
				} );
				cleanups.push( () => accentEls.forEach( ( el ) => el.remove() ) );
			}

			/*
			 * The signature moment (RESEARCH Pattern 3), a single scroll-driven sequence.
			 * Per the client rework the bloom is NOT auto-played on load: at rest (the
			 * scrubbed timeline at progress 0) the visitor sees JUST THE BOWL (the
			 * .fc-vessel). Both the flat .fc-petal AND the photographic .fc-petal-photo skin
			 * rest hidden (their gsap.from / fromTo apply opacity 0 at progress 0), and the
			 * .fc-thread rest undrawn. The FIRST thing scrolling does is the BUILD: the
			 * pinned bloomTl below blooms the leaves OUT OF THE BOWL (flat petals scale up
			 * from the vessel, the spice skin blooms in and settles, threads draw) into the
			 * full photographic spice bloom, then holds, grows and hands off. Every
			 * from-state lives in the scrubbed timeline (never in CSS); the flat mark
			 * geometry is untouched, so no-JS and reduced-motion keep the static <img> mark.
			 */
			/*
			 * Modest fixed grow (Part B). The mark grows to GROW_SCALE (a fixed ~3x, a
			 * prominent flourish rather than a screen-fill; the TRAVEL beat below then
			 * carries it into Story). centerDy shifts the mark to the exact
			 * viewport centre so the grow stays centred; it is a FUNCTION value, so
			 * invalidateOnRefresh (below) re-measures it against the live viewport on every
			 * ScrollTrigger refresh (resize). gsap reverts to progress 0 before a refresh,
			 * so it reads the mark at its RESTING size, never mid-grow. markEl is the hero
			 * morph anchor (its injected SVG scales as a unit); guarded because the anchor
			 * can be absent.
			 */
			const markEl = document.querySelector( '[data-fc-morph="bloom"]' );
			const storySection = document.querySelector( '[data-fc-scene="story"]' );
			const storySlot = document.querySelector( '[data-fc-morph-target="story"]' );
			const storySlotImg = storySlot ? storySlot.querySelector( 'img' ) : null;
			const centerDy = () => {
				// Measure the mark's centre offset from the HERO top (stable regardless of
				// scroll position, unlike the mark's live viewport rect which ScrollTrigger's
				// refresh can read mid-scroll). The hero pins at top:0, so moving the mark
				// centre to half the viewport height lands the zoomed logo dead-centre.
				const heroTop = hero.getBoundingClientRect().top;
				const m = markEl.getBoundingClientRect();
				const centreFromHeroTop = ( m.top - heroTop ) + m.height / 2;
				// Land the zoomed logo LOWER than centre (62% down) so it fills more of the
				// lower screen and the gap to Story below reads smaller. Dialable.
				return window.innerHeight * 0.62 - centreFromHeroTop;
			};

			/*
			 * Landing geometry for the TRAVEL beat. CRITICALLY these use offsetLeft /
			 * offsetTop / offsetWidth (layout values, immune to transforms), NEVER
			 * getBoundingClientRect: gsap evaluates function-based tween values lazily at
			 * the tween's first render, which for the travel happens MID-PIN with the grow
			 * transform already applied, so a rect there would read the 3.6x grown mark
			 * and land at a third of the intended size. Offset math is correct no matter
			 * when it runs. The mark is measured relative to the HERO (the hero pins at
			 * top:0 left:0, so during the pin the mark's untransformed viewport position
			 * equals its offset within the hero), and the slot relative to the STORY
			 * section (the fc-hero-morph pull lands Story's top exactly at the viewport
			 * top at the pin end, so the slot's offset WITHIN Story is its viewport
			 * position at the final frame). invalidateOnRefresh re-runs the functions on
			 * every resize. The flight aims at the slot's FINAL-frame position (a constant,
			 * never scroll-coupled): the mark arrives and holds there while Story rises the
			 * last stretch up into it, so scrub lag can never misplace the landing.
			 */
			const offsetWithin = ( el, root ) => {
				let x = 0;
				let y = 0;
				let node = el;
				while ( node && node !== root && node !== document.body ) {
					x += node.offsetLeft;
					y += node.offsetTop;
					node = node.offsetParent;
				}
				if ( node !== root ) {
					// The chain surfaced at body without meeting root (root is not a
					// positioned ancestor): rebase both to the page and subtract.
					const rootAbs = offsetWithin( root, document.body );
					x -= rootAbs.x;
					y -= rootAbs.y;
				}
				return { x, y };
			};
			const landDelta = () => {
				const m = offsetWithin( markEl, hero );
				const s = offsetWithin( storySlot, storySection );
				return {
					x: ( s.x + storySlot.offsetWidth / 2 ) - ( m.x + markEl.offsetWidth / 2 ),
					y: ( s.y + storySlot.offsetHeight / 2 ) - ( m.y + markEl.offsetHeight / 2 ),
					scale: storySlot.offsetWidth / markEl.offsetWidth,
				};
			};

			/*
			 * The scrubbed pin runs the WHOLE sequence now: BUILD, brief hold, copy lift +
			 * grow, travel into Story, landing. One pin (HERO_PIN), one trigger, start
			 * 'top top'; scrub ties every beat to scroll and makes it fully reversible
			 * (scrolling up un-seats the emblem, flies the bloom back up, shrinks it, then
			 * re-furls it back to the flat mark). invalidateOnRefresh re-measures the grow
			 * centre AND the landing geometry on resize. The BUILD (petals + threads) and
			 * the grow/travel (the mark container) target different properties, so they
			 * compose without fighting.
			 */
			const bloomTl = gsap.timeline( {
				scrollTrigger: {
					trigger: '[data-fc-scene="hero"]',
					start: 'top top',
					end: HERO_PIN,
					pin: true,
					scrub: 1,
					anticipatePin: 1,
					invalidateOnRefresh: true,
				},
			} );

			/*
			 * BUILD (beat 1, scroll-driven): the FIRST thing scrolling does is bloom the
			 * leaves OUT OF THE BOWL into the full photographic spice bloom. At rest only the
			 * .fc-vessel shows. The .fc-thread draw in (drawSVG from 0%); the flat .fc-petal
			 * scale up from the vessel (from scale 0.2, opacity 0, about the 48,53 pivot);
			 * the photographic .fc-petal-photo skin blooms in from opacity 0 + scale 0.2 so
			 * it emerges from the bowl too; and the .fc-petal-photos group settles from a
			 * hair oversized (no blur filter: a filter forces a rasterised buffer, which is
			 * exactly what went soft under the grow). All are gsap.from / fromTo, so their
			 * from-states apply at progress 0
			 * (the rest frame is JUST THE BOWL, never leaves-already-in-place). Positioned
			 * within the first D_BUILD units, so the build owns roughly the first third.
			 */
			bloomTl
				.from( '[data-fc-morph="bloom"] .fc-thread', {
					drawSVG: '0%',
					stagger: 0.06,
					duration: 0.5,
					ease: 'power1.inOut',
				}, 0 )
				.from( '[data-fc-morph="bloom"] .fc-petal', {
					opacity: 0,
					scale: 0.2,
					transformOrigin: '48px 53px',
					stagger: 0.06,
					duration: 0.55,
					ease: 'back.out(1.4)',
				}, 0 )
				.fromTo(
					'[data-fc-morph="bloom"] .fc-petal-photo',
					{ opacity: 0, scale: 0.2, transformOrigin: '48px 53px' },
					{ opacity: 1, scale: 1, transformOrigin: '48px 53px', stagger: 0.06, duration: 0.55, ease: 'back.out(1.4)' },
					0.05
				)
				.from( '[data-fc-morph="bloom"] .fc-petal-photos', {
					scale: PETAL_PHOTO_SETTLE,
					transformOrigin: '48px 53px',
					duration: D_BUILD,
					ease: 'power2.out',
				}, 0 );

			/*
			 * Part B core: after the BUILD and the brief hold, GROW the formed bloom mark to the modest
			 * fixed GROW_SCALE and centre it (a snappy flourish), having first lifted the
			 * copy + accents + field away at COPY_AT so nothing floats over the growing mark.
			 * The copy is ROCK-SOLID through the build + hold (it is only ever hidden here, by
			 * the desktop autoAlpha tween, so no-JS and reduced-motion keep it visible). autoAlpha
			 * sets visibility:hidden at opacity 0, so the faded CTAs cannot take focus or clicks.
			 * The grow sits at GROW_AT on the ONE pinned timeline (no second pin); the
			 * copy/accents/field lift just before it, at COPY_AT.
			 */
			if ( markEl ) {
				bloomTl.to( markEl, {
					scale: GROW_SCALE,
					y: centerDy,
					transformOrigin: 'center center',
					ease: 'none',
					duration: D_GROW,
					/*
					 * force3D:false keeps this a 2D matrix. GSAP's default "auto" swaps in a
					 * translate3d during the tween, which promotes the mark to a compositor
					 * layer: the GPU then stretches one cached raster instead of re-rendering
					 * the SVG, so the growing mark went soft. 2D transforms re-render the
					 * vector at every scale, which is why the mark is SVG in the first place.
					 */
					force3D: false,
				}, GROW_AT );

				/*
				 * RESOLVE-ON-GROW (2026-07-23, client direction). The photographic skin is a
				 * 512px raster per petal; blown up to ~570px (and double that on a retina
				 * screen) it read as muddy, dark and soft next to the razor-sharp vector
				 * vessel, and it buried the brand petal colours under kale-and-chilli
				 * photography. So the skin now crossfades OFF over the first half of the
				 * grow: the bloom still BUILDS photographically at rest size (the signature
				 * made-of-food moment, where the texture is crisp), then resolves into the
				 * flat saffron/teal/clay vector mark exactly as it becomes big. Everything
				 * the visitor sees at size is scalable SVG in the proper brand colours.
				 * This used to sit at TRAVEL_AT; moving it earlier is what fixes the grow.
				 */
				bloomTl.to( '[data-fc-morph="bloom"] .fc-petal-photo', {
					opacity: 0,
					ease: 'power1.inOut',
					duration: D_GROW * 0.55,
				}, GROW_AT );
			}

			bloomTl.to( '.fc-home-hero__headline, .fc-home-hero__subline, .fc-home-hero__actions', {
				autoAlpha: 0,
				y: '-3vh',
				ease: 'power1.in',
				duration: D_COPY,
			}, COPY_AT );

			if ( accentEls.length ) {
				bloomTl.to( accentEls, {
					autoAlpha: 0,
					ease: 'none',
					duration: D_COPY,
				}, COPY_AT );
			}

			/*
			 * The REVEAL, reworked for the landing: the travelling mark is a CHILD of the
			 * hero, so the hero itself can never fade (parent opacity would take the mark
			 * with it). Instead the hero's opaque LAYERS fade individually DURING the
			 * flight: the cream background-color clears, the warm field lifts, and the
			 * __inner::before legibility veil drops (via its custom property, since
			 * pseudo-elements can not be tweened directly). Story therefore appears
			 * AROUND the moving mark rather than after it. Because Story is flat and
			 * BEHIND the z-indexed hero, it stays hidden until these fades. A .set at the
			 * fade end drops the hero's pointer events, so the now-transparent pinned box
			 * never blocks Story's links (scrub-reversible). Desktop-motion-only; all of
			 * it reverts with the gsap context on teardown.
			 */
			if ( hero ) {
				const heroBgClear = () => {
					const parts = getComputedStyle( hero ).backgroundColor.match( /[\d.]+/g ) || [ 244, 238, 223 ];
					return `rgba(${ parts[ 0 ] }, ${ parts[ 1 ] }, ${ parts[ 2 ] }, 0)`;
				};
				bloomTl.to( hero, {
					backgroundColor: heroBgClear,
					ease: 'power1.in',
					duration: D_REVEAL,
				}, REVEAL_AT );
				const heroInner = hero.querySelector( '.fc-home-hero__inner' );
				if ( heroInner ) {
					bloomTl.to( heroInner, {
						'--fc-hero-veil': 0,
						ease: 'power1.in',
						duration: D_REVEAL,
					}, REVEAL_AT );
				}
				if ( heroField ) {
					bloomTl.to( heroField, {
						autoAlpha: 0,
						ease: 'power1.in',
						duration: D_REVEAL,
					}, REVEAL_AT );
				}
				bloomTl.set( hero, { pointerEvents: 'none' }, REVEAL_AT + D_REVEAL );
			}

			/*
			 * The TRAVEL + LANDING (the signature moment). The grown bloom resolves its
			 * photo skin back to the flat brand mark mid-flight, shrinks and glides to the
			 * emblem slot's FINAL-frame position and holds there while Story rises the
			 * last stretch up into it; the crossfade then closes exactly at the pin end.
			 * The destination is a constant (never coupled to the live story position):
			 * scroll-coupling the landing was the first cut, and the scrub lag made the
			 * mark seat visibly low before sliding up to correct. With a fixed target the
			 * lag only ever delays the flight, never bends it, and the end-of-timeline
			 * swap renders against Story already parked at its final frame. If the slot is
			 * ever absent the mark falls back to the plain dissolve, so the pin always
			 * resolves.
			 */
			if ( markEl && storySlot && storySlotImg && storySection && hero ) {
				// Hide the static emblem up front (desktop only): the travelling bloom IS
				// the logo until it lands. This from-state lives here in JS, never in CSS,
				// so no-JS / mobile / reduced-motion render the emblem statically.
				gsap.set( storySlotImg, { autoAlpha: 0 } );

				// The photo skin is already resolved to the flat vector mark by the grow
				// (see RESOLVE-ON-GROW above), so the flight has no skin left to fade: what
				// travels to Story is the brand-coloured vector, crisp the whole way.
				bloomTl.to( markEl, {
					x: () => landDelta().x,
					y: () => landDelta().y,
					scale: () => landDelta().scale,
					transformOrigin: 'center center',
					ease: 'power1.inOut',
					duration: D_TRAVEL,
					force3D: false, // same reason as the grow: keep it re-rendering as vector, never a stretched cache.
				}, TRAVEL_AT );

				// Matched-geometry crossfade, closing exactly at TL_END: the static emblem
				// eases in under the held mark, the inline bloom releases a breath later.
				bloomTl.to( storySlotImg, { autoAlpha: 1, ease: 'none', duration: D_LAND }, LAND_AT );
				bloomTl.to( markEl, { autoAlpha: 0, ease: 'none', duration: D_LAND }, TL_END - D_LAND );
			} else if ( markEl ) {
				bloomTl.to( markEl, {
					autoAlpha: 0,
					ease: 'power1.in',
					duration: D_LAND,
				}, LAND_AT );
			}

			/*
			 * Story's choreographed entrance rides the same pin: its children rise gently
			 * while the hero layers fade, so Story is fully presented before the pin
			 * releases. CHILDREN only: the Story section itself stays untransformed and
			 * flat, which the shared-scroll handoff depends on (a transform would stack
			 * Story over the pinned hero). The rise finishes before LAND_AT, so the
			 * landing slot is seated against settled geometry.
			 */
			if ( storySection ) {
				const storyKids = gsap.utils.toArray( '.fc-home-story__inner > *' );
				if ( storyKids.length ) {
					bloomTl.from( storyKids, {
						autoAlpha: 0,
						y: 24,
						ease: 'power2.out',
						duration: 0.35,
						stagger: 0.05,
					}, TRAVEL_AT );
				}
			}

			/*
			 * Gentle DESKTOP-ONLY parallax (RESEARCH decision, Pitfall 6): the warm
			 * field drifts slightly slower than scroll through the pinned hold, adding
			 * depth while the hero bloom stays the star. A small constant scale gives
			 * overflow room so the subtle translate never reveals an edge. Mobile
			 * drops parallax entirely (the most expensive touch effect).
			 */
			if ( heroField ) {
				const fieldImg = heroField.querySelector( '.fc-hero-field__img' );
				if ( fieldImg ) {
					gsap.fromTo(
						fieldImg,
						{ yPercent: -6, scale: 1.15 },
						{
							yPercent: 6,
							scale: FIELD_BREATHE_TO,
							ease: 'none',
							scrollTrigger: {
								trigger: '[data-fc-scene="hero"]',
								start: 'top top',
								end: '+=80%',
								scrub: true,
							},
						}
					);

					/*
					 * Fade the warm field back on the pinned timeline at COPY_AT (with the copy,
					 * just before the grow), so it does not float over the growing mark. Opacity
					 * only (the fromTo above owns yPercent + scale on its own trigger, so there is
					 * no conflict); this lives inside isDesktop, so reduced-motion / mobile never
					 * run it.
					 */
					bloomTl.to( fieldImg, {
						opacity: 0.1,
						ease: 'none',
						duration: D_COPY,
					}, COPY_AT );
				}
			}
		}

		if ( isMobile ) {
			/*
			 * Mobile (RESEARCH Pattern 5, Pitfall 5): the same bloom unfurls IN FLOW
			 * as the hero scrolls past, with NO pin (a pinned trigger would thrash
			 * against the mobile URL-bar show/hide), NO warm field and NO parallax.
			 * A light scrub keeps it tied to scroll and reversible; the stagger is
			 * shorter and quicker than desktop so touch stays smooth. Element-relative
			 * starts only, never vh distances. Its END is the same resolved bloom, so
			 * mobile, desktop and motion-off all share the one logo-mark end-state.
			 */
			/*
			 * clamp() pins both edges inside the real scroll range: the hero sits at
			 * the top of the page, so a bare 'top 80%' resolves ABOVE scroll 0 and the
			 * scrub arrives pre-consumed (~40% at load), which rendered the bloom
			 * already assembled instead of starting as the empty vessel. Clamped, the
			 * build owns the first real scroll and the load state is the bare mark.
			 */
			const mobileTl = gsap.timeline( {
				scrollTrigger: {
					trigger: '[data-fc-scene="hero"]',
					start: 'clamp(top 80%)',
					end: 'clamp(bottom 55%)',
					scrub: 0.5,
				},
			} );
			mobileTl
				.from( '[data-fc-morph="bloom"] .fc-thread', {
					drawSVG: '0%',
					stagger: 0.05,
					ease: 'none',
				} )
				.from( '[data-fc-morph="bloom"] .fc-petal', {
					scale: 0.4,
					opacity: 0,
					transformOrigin: '48px 53px',
					stagger: 0.04,
					ease: 'back.out(1.2)',
				}, '<0.1' );
		}

		/*
		 * Wave 3 supporting motion, in BOTH motion branches (RESEARCH Pattern 6):
		 * the section rise-and-fade reveals, the staggered service-card and
		 * timeline-step cascades, the slow seamless ticker drift, the timeline
		 * connector line-draw with node reveal, and the understated closing-CTA
		 * reprise breath. (Desktop parallax lives in the isDesktop branch above;
		 * mobile drops it.) Every from-state lives in gsap.from (never in CSS); the
		 * reduce branch above returned early, so none of this runs under reduced motion.
		 */
		buildReveals( isDesktop ); // desktop omits the Story reveal (the morph owns it; a transform there would leak Story over the pinned hero).
		buildMarquee( cleanups );
		buildTimelineDraw( cleanups );
		buildReprise();

		return () => {
			svg?.remove();
			heroField?.remove();
			cleanups.forEach( ( fn ) => fn() );
		};
	}
);
